<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import type { ExpressionKey } from '@/js/consts/language/en';
    import {
        EchoFilterCheckbox,
        FilterKeyToPropertyMap,
        FilterStoreName,
        isCheckboxFilterType,
    } from '@/js/services/api/filter/types';
    import { clearCheckboxSelection, hasSelectedDescendant } from '@/js/services/api/filter/utils';
    import EchoButton from '../EchoButton.vue';
    import EchoTooltip from '../EchoTooltip.vue';
    import CheckboxFilter from '../filter/CheckboxFilter.vue';
    import SearchableCheckboxFilter from '../filter/SearchableCheckboxFilter.vue';
    import AllFiltersItem from '../filter/AllFiltersItem.vue';
    import MultiSelectInput from '../MultiSelectInput.vue';
    import SentimentFilter from '../filter/SentimentFilter.vue';
    import TrackerFilter from '../filter/TrackerFilter.vue';
    import EchoPopover from '../EchoPopover.vue';
    import ResolvedIssuesFilter from '../filter/ResolvedIssuesFilter.vue';
    import { ChevronDownIcon } from '@lucide/vue';

    type FilterKey = keyof FilterKeyToPropertyMap;

    const props = defineProps<{
        translationKey: ExpressionKey;
        filterKey: FilterKey;
        dynamicFilterKeyName?: string;
        type?: 'Text' | 'Checkbox';
        filterStoreName: FilterStoreName;
    }>();

    const filterStore = useFilterStoreByName(props.filterStoreName);

    const checkboxFilters: FilterKey[] = [
        'Channel',
        'Queue',
        'Skill',
        'ProtocolCoachedByName',
        'ProtocolAgentName',
        'ProtocolGrade',
        'Category',
    ];

    const sentimentFilters: FilterKey[] = ['SentimentAgent', 'SentimentCustomer'];

    const sentimentDirectionFilters: FilterKey[] = [
        'SentimentAgentDirection',
        'SentimentCustomerDirection',
    ];

    const booleanFilters: FilterKey[] = [
        'ProtocolCreatedByAi',
        'ProtocolSubmittedByMe',
        'ProtocolPendingApproval',
        'ProtocolActionRequired',
    ];

    const getFilterPropertyName = (key: FilterKey): FilterKeyToPropertyMap[FilterKey] | null => {
        const map = filterStore.all?.filterKeyToPropertyMap;
        if (!map) return null;

        return map[key] || null;
    };

    const getFilterCheckboxData = computed((): EchoFilterCheckbox[] | null => {
        const propertyName = getFilterPropertyName(props.filterKey);

        if (!filterStore.all || !propertyName) return null;

        return filterStore.all[
            propertyName as keyof typeof filterStore.all
        ] as EchoFilterCheckbox[];
    });

    const getCustomFilter = computed(() => {
        if (!filterStore.all || !props.dynamicFilterKeyName) return null;

        return filterStore.all.customDataFilter?.find((f) => f.name === props.dynamicFilterKeyName);
    });

    const createBooleanFilterCheckbox = (id: boolean): EchoFilterCheckbox => {
        let label = id === true ? __('yes') : __('no');
        let isSelected = false;

        const propertyName = getFilterPropertyName(props.filterKey);
        if (propertyName) {
            isSelected = filterStore.all?.[propertyName as keyof typeof filterStore.all] === id;
        }

        if (props.filterKey === 'ProtocolCreatedByAi') {
            label = id === true ? __('ai-buddy') : __('manual');
        } else if (props.filterKey === 'ProtocolPendingApproval') {
            label = id === true ? __('protocol-pending-approval') : __('protocol-approved');
        } else if (props.filterKey === 'ProtocolActionRequired') {
            label =
                id === true
                    ? __('table-protocol-column-action-required')
                    : __('table-protocol-column-no-action-required');
        }

        return {
            id,
            isSelected,
            isGroup: false,
            isDisabled: false,
            isHidden: false,
            name: label,
            children: [],
        };
    };

    const getBooleanFilterCheckboxes = ref<EchoFilterCheckbox[]>(
        props.filterKey === 'ProtocolCreatedByAi'
            ? [
                  createBooleanFilterCheckbox(true),
                  createBooleanFilterCheckbox(false),
                  {
                      id: 'ai-edited',
                      isSelected: filterStore.all?.editedByHuman === true,
                      isDisabled: false,
                      isGroup: false,
                      isHidden: false,
                      name: __('ai-human-edited'),
                      children: [],
                  },
              ]
            : [createBooleanFilterCheckbox(true), createBooleanFilterCheckbox(false)]
    );

    const updateBooleanFilter = (value: boolean | null) => {
        if (!filterStore.all) return;

        if (props.filterKey === 'ProtocolSubmittedByMe') {
            filterStore.all.submittedByMe = value;
        } else if (props.filterKey === 'ProtocolPendingApproval') {
            filterStore.all.pendingApproval = value;
        } else if (props.filterKey === 'ProtocolActionRequired') {
            filterStore.all.actionRequired = value;
        }
    };

    watch(
        () => getBooleanFilterCheckboxes.value,
        (newCheckboxes) => {
            if (props.filterKey === 'ProtocolCreatedByAi') {
                if (!filterStore.all) return;
                const aiEdited = newCheckboxes.find((cb) => cb.id === 'ai-edited');
                const aiBuddy = newCheckboxes.find((cb) => cb.id === true);
                const manual = newCheckboxes.find((cb) => cb.id === false);

                if (aiEdited?.isSelected) {
                    if (aiBuddy) aiBuddy.isSelected = false;
                    if (manual) manual.isSelected = false;
                    filterStore.all.createdByAi = null;
                    filterStore.all.editedByHuman = true;
                } else if (aiBuddy?.isSelected && !manual?.isSelected) {
                    filterStore.all.createdByAi = true;
                    filterStore.all.editedByHuman = null;
                } else if (manual?.isSelected && !aiBuddy?.isSelected) {
                    filterStore.all.createdByAi = false;
                    filterStore.all.editedByHuman = null;
                } else {
                    filterStore.all.createdByAi = null;
                    filterStore.all.editedByHuman = null;
                }
                return;
            }

            const selectedItems = newCheckboxes.filter((checkbox) => checkbox.isSelected);

            if (selectedItems.length === 0 || selectedItems.length === newCheckboxes.length) {
                updateBooleanFilter(null);
            } else {
                const selectedCheckbox = selectedItems.find((item) => item.isSelected);
                updateBooleanFilter(!!selectedCheckbox?.id);
            }
        },
        { deep: true }
    );

    const isCheckboxStyleFilter = computed(
        () =>
            checkboxFilters.includes(props.filterKey) ||
            sentimentFilters.includes(props.filterKey) ||
            sentimentDirectionFilters.includes(props.filterKey)
    );

    // A partially-selected group has isSelected === false on itself, so checking isSelected
    // alone would miss it — hasSelectedDescendant covers that case.
    const isAnySelected = (checkboxes: EchoFilterCheckbox[]) =>
        checkboxes.some((checkbox) => checkbox.isSelected || hasSelectedDescendant(checkbox));

    // Null when the current filterKey isn't checkbox-shaped (OnlyResolved, Text).
    const getResettableCheckboxes = (): EchoFilterCheckbox[] | null => {
        if (isCheckboxStyleFilter.value) return getFilterCheckboxData.value;
        if (booleanFilters.includes(props.filterKey)) return getBooleanFilterCheckboxes.value;
        if (props.filterKey === 'Tracker') return filterStore.all?.trackerFilter?.filter ?? null;

        if (getCustomFilter.value && isCheckboxFilterType(getCustomFilter.value.filterType)) {
            return (getCustomFilter.value.filter as EchoFilterCheckbox[]) ?? null;
        }

        return null;
    };

    const hasActiveFilter = computed(() => {
        const checkboxes = getResettableCheckboxes();
        if (checkboxes) return isAnySelected(checkboxes);

        if (props.filterKey === 'OnlyResolved') {
            return (
                filterStore.all?.onlyResolved != null || filterStore.onlyResolvedBothCheckedAsNull
            );
        }

        if (getCustomFilter.value?.filterType === 'Text') {
            return (getCustomFilter.value.words?.length ?? 0) > 0;
        }

        return false;
    });

    const hasFilter = computed(() => {
        return (
            isCheckboxStyleFilter.value ||
            props.filterKey === 'OnlyResolved' ||
            props.filterKey === 'Tracker' ||
            booleanFilters.includes(props.filterKey) ||
            !!getCustomFilter.value
        );
    });

    const onReset = () => {
        const checkboxes = getResettableCheckboxes();
        if (checkboxes) {
            clearCheckboxSelection(checkboxes);
            return;
        }

        if (props.filterKey === 'OnlyResolved') {
            if (!filterStore.all) return;
            filterStore.all.onlyResolved = null;
            filterStore.onlyResolvedBothCheckedAsNull = false;
            return;
        }

        if (getCustomFilter.value?.filterType === 'Text') {
            getCustomFilter.value.words = [];
        }
    };

    const labelText = computed(() =>
        props.dynamicFilterKeyName
            ? props.dynamicFilterKeyName
            : __untypedExpression(props.translationKey)
    );

    const labelElWithFilter = ref<HTMLElement | null>(null);
    const labelElWithoutFilter = ref<HTMLElement | null>(null);
    const isTruncated = ref(false);

    const checkTruncation = (el: HTMLElement | null) => {
        if (el) isTruncated.value = el.scrollHeight > el.clientHeight;
    };

    watch(labelElWithFilter, (el) => checkTruncation(el), { flush: 'post' });
    watch(labelElWithoutFilter, (el) => checkTruncation(el), { flush: 'post' });
</script>
<template>
    <EchoTooltip :label="labelText" :disabled="!isTruncated">
        <div class="w-full min-w-0 overflow-hidden">
            <EchoPopover v-if="hasFilter" position="bottom-end" panel-class="ColumnFilter__popover">
                <template #button="{ anchor, togglePopover, buttonAttrs }">
                    <div class="w-full min-w-0 overflow-hidden">
                        <EchoButton
                            :ref="anchor"
                            :data-has-filter="`${filterKey} ${getFilterPropertyName(props.filterKey)}`"
                            variant="tertiary"
                            size="small"
                            v-bind="buttonAttrs"
                            @click="togglePopover"
                        >
                            <span
                                ref="labelElWithFilter"
                                class="text-overline-sm line-clamp-2 wrap-break-word whitespace-normal text-left"
                            >
                                {{ labelText }}
                            </span>
                            <template #icon-right>
                                <span class="relative inline-flex mt-0.75">
                                    <ChevronDownIcon class="size-3.5" />
                                    <span
                                        v-if="hasActiveFilter"
                                        class="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary-beet"
                                    />
                                </span>
                            </template>
                        </EchoButton>
                    </div>
                </template>
                <template #popover>
                    <div class="-m-4 column-filter-popup">
                        <div class="w-auto min-w-54 max-w-72 flex flex-col">
                            <div class="px-4 pt-3.5 pb-3">
                                <h3 class="text-overline-sm text-gray-medium">{{ labelText }}</h3>
                            </div>
                            <div
                                :class="
                                    getCustomFilter?.filterType === 'Text'
                                        ? ''
                                        : 'max-h-96 overflow-auto custom-scrollbar'
                                "
                            >
                                <div class="overflow-hidden">
                                    <div class="fp-content">
                                        <CheckboxFilter
                                            v-if="
                                                checkboxFilters.includes(props.filterKey) &&
                                                getFilterCheckboxData
                                            "
                                            :checkboxes="getFilterCheckboxData"
                                            :expression-base="
                                                props.filterKey === 'Channel'
                                                    ? 'channel-name-'
                                                    : undefined
                                            "
                                        />

                                        <CheckboxFilter
                                            v-if="booleanFilters.includes(props.filterKey)"
                                            :checkboxes="getBooleanFilterCheckboxes"
                                        />

                                        <ResolvedIssuesFilter
                                            v-if="props.filterKey === 'OnlyResolved'"
                                            :filter-store-name="props.filterStoreName"
                                        />

                                        <TrackerFilter
                                            v-if="props.filterKey === 'Tracker'"
                                            :filter-store-name="props.filterStoreName"
                                        />

                                        <div
                                            v-if="
                                                sentimentFilters.includes(props.filterKey) &&
                                                getFilterCheckboxData
                                            "
                                            class="-mb-3.5 -mt-2.5"
                                        >
                                            <SentimentFilter
                                                :checkboxes="getFilterCheckboxData"
                                                type="sentiment"
                                            />
                                        </div>

                                        <div
                                            v-if="
                                                sentimentDirectionFilters.includes(
                                                    props.filterKey
                                                ) && getFilterCheckboxData
                                            "
                                            class="-mb-3.5 -mt-2.5"
                                        >
                                            <SentimentFilter
                                                :checkboxes="getFilterCheckboxData"
                                                type="direction"
                                            />
                                        </div>

                                        <AllFiltersItem v-if="getCustomFilter">
                                            <template #content>
                                                <CheckboxFilter
                                                    v-if="getCustomFilter.filterType === 'Checkbox'"
                                                    :checkboxes="
                                                        getCustomFilter.filter as EchoFilterCheckbox[]
                                                    "
                                                />
                                                <SearchableCheckboxFilter
                                                    v-if="
                                                        getCustomFilter.filterType ===
                                                        'SearchableCheckbox'
                                                    "
                                                    v-model:filter="getCustomFilter.filter"
                                                    :custom-data-type-id="getCustomFilter.key"
                                                />
                                                <div
                                                    v-if="getCustomFilter.filterType === 'Text'"
                                                    class="px-4 pb-6"
                                                >
                                                    <MultiSelectInput
                                                        :id="
                                                            'customFilterText_' +
                                                            getCustomFilter.key
                                                        "
                                                        :items="getCustomFilter.words || []"
                                                        :placeholder="__('search')"
                                                        size="small"
                                                        @update:items="
                                                            getCustomFilter.words = $event
                                                        "
                                                    />
                                                </div>
                                            </template>
                                        </AllFiltersItem>
                                    </div>
                                </div>
                            </div>
                            <div class="mx-3.5 border-t border-t-gray-lightest"></div>
                            <div class="flex items-center justify-end px-3.5 pt-2 pb-3">
                                <EchoButton variant="secondary" size="small" @click="onReset">
                                    {{ __('reset') }}
                                </EchoButton>
                            </div>
                        </div>
                    </div>
                </template>
            </EchoPopover>

            <div
                v-else
                :data-missing-filter="`${filterKey} ${getFilterPropertyName(props.filterKey)}`"
                class="Button Button--small Button--tertiary cursor-default min-w-0 overflow-hidden"
            >
                <span
                    ref="labelElWithoutFilter"
                    class="text-overline-sm line-clamp-2 wrap-break-word whitespace-normal text-left"
                >
                    {{ labelText }}
                </span>
            </div>
        </div>
    </EchoTooltip>
</template>

<style scoped>
    /* :deep() targets the checkbox-row labels directly, so the components that render them don't need a coupling class. */
    .column-filter-popup :deep(label.text-body) {
        width: calc(100% - 4px);
        margin-inline: 2px;
        margin-bottom: 4px;
        padding-left: 6px;
        border-radius: 8px;
    }

    .column-filter-popup :deep(.pb-5) {
        padding-bottom: 12px;
    }

    .column-filter-popup :deep(label.text-body:hover) {
        background-color: var(--color-primary-plum-50);
    }
</style>
